import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import Hls from 'hls.js';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import {
  IoChevronBack,
  IoChevronForward,
  IoArrowBack,
  IoTimeOutline,
  IoRefresh,
  IoSend,
} from 'react-icons/io5';
import { LuFileText } from 'react-icons/lu';

import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import RichTranscript from '../../components/videos/RichTranscript';
import WitnessLobbyistAssignment from '../../components/videos/WitnessLobbyistAssignment';
import MainLoader from '../../components/Structures/MainLoader';

import { getVideoDetails } from '../../features/video/videoSlice';

const formatRecordedTimestamp = (seconds) => {
  const safe = Math.max(0, Math.floor(seconds || 0));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const remaining = safe % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(remaining).padStart(2, '0')}`;
  }
  return `${minutes}:${String(remaining).padStart(2, '0')}`;
};

const formatCommitteeBillLabel = (bill) => {
  if (!bill) return 'Unknown bill';
  const trimmed = bill.trim();
  const parts = trimmed.split('-');
  if (parts.length >= 4) {
    const type = parts[2]?.toUpperCase();
    const number = parts.slice(3).join('-').toUpperCase();
    if (type && number) return `${type} ${number}`;
  }
  return trimmed;
};

const getCommitteeActionDescription = (action) => {
  const desc = action.ActionDescription?.trim();
  if (desc) return desc;
  const raw = action.Action?.trim();
  if (raw && !/^\d+$/.test(raw)) return raw;
  return 'Action details unavailable';
};

const convertTimeToSeconds = (timeString) => {
  const parts = timeString.split(':');
  const hours = parseInt(parts[0] || '0', 10);
  const minutes = parseInt(parts[1] || '0', 10);
  const seconds = parseInt(parts[2] || '0', 10);
  return hours * 3600 + minutes * 60 + seconds;
};

const Video = () => {
  const { videoId, state, searchTerm } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { videoDetails, isLoadingDetails } = useSelector((s) => s.video);
  const data = videoDetails;

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const messagesEndRef = useRef(null);

  const [userExpandedVideo, setUserExpandedVideo] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [activeTab, setActiveTab] = useState('assistant');
  const [activeRightTab, setActiveRightTab] = useState('transcript');

  const [aiChat, setAiChat] = useState([
    { Id: 0, message: 'Hi! You can ask me whatever you want about the video.' },
  ]);
  const [userQuestion, setUserQuestion] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const isVideoExpanded = isSmallScreen || userExpandedVideo;

  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const startParam = urlParams.get('start');
  const queryText = urlParams.get('q') ?? urlParams.get('searchText') ?? searchTerm ?? '';

  useEffect(() => {
    if (videoId && state) {
      dispatch(getVideoDetails({ videoId, state }));
    }
  }, [videoId, state, dispatch]);

  useEffect(() => {
    const checkScreenSize = () => setIsSmallScreen(window.innerWidth < 1280);
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    setActiveTab(isVideoExpanded ? 'transcript' : 'assistant');
  }, [isVideoExpanded]);

  useEffect(() => {
    const url = data?.videoDetails?.url;
    const video = videoRef.current;
    if (!url || !video) return;

    const startSeconds = startParam ? parseFloat(startParam) / 10 : 0;
    const setStart = () => {
      if (startSeconds > 0) {
        video.currentTime = startSeconds;
        video.play().catch(() => {});
      }
    };

    const isHls = url.includes('.m3u8');
    if (isHls && Hls.isSupported()) {
      hlsRef.current = new Hls();
      hlsRef.current.loadSource(url);
      hlsRef.current.attachMedia(video);
      video.addEventListener('loadedmetadata', setStart);
      return () => {
        if (hlsRef.current) hlsRef.current.destroy();
        video.removeEventListener('loadedmetadata', setStart);
      };
    }

    if (video.canPlayType('application/vnd.apple.mpegurl') || !isHls) {
      video.src = url;
      video.addEventListener('loadedmetadata', setStart);
      return () => {
        video.removeEventListener('loadedmetadata', setStart);
      };
    }
  }, [data?.videoDetails?.url, startParam]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiChat]);

  const onGoToTimeStamp = (timeIn) => {
    const t = typeof timeIn === 'string' ? parseFloat(timeIn) : timeIn;
    if (Number.isFinite(t) && videoRef.current) {
      videoRef.current.currentTime = t / 10;
      videoRef.current.play().catch(() => {});
    }
  };

  const onGoToRecordedVideoTime = (timeIn) => {
    const t = typeof timeIn === 'string' ? parseFloat(timeIn) : timeIn;
    if (Number.isFinite(t) && videoRef.current) {
      videoRef.current.currentTime = Math.max(0, t);
      videoRef.current.play().catch(() => {});
    }
  };

  const handleWitnessClick = (startTimeStr) => {
    const seconds = convertTimeToSeconds(startTimeStr);
    onGoToRecordedVideoTime(seconds);
  };

  const handleSubmitQuestion = async () => {
    const message = userQuestion.trim();
    if (!message || aiLoading) return;
    const updated = [...aiChat, { Id: 'user', message }];
    setAiChat(updated);
    setAiLoading(true);
    setUserQuestion('');
    try {
      const { data: aiData } = await axios.post('/api/video/ask', {
        query: message,
        videoId: Number(videoId),
      });
      const response = typeof aiData === 'string' ? aiData : aiData?.content || '';
      setAiChat([...updated, { Id: 0, message: response }]);
    } catch (err) {
      setAiChat([
        ...updated,
        { Id: 0, message: 'Sorry, the AI assistant is temporarily unavailable.' },
      ]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleDownloadTranscript = () => {
    const transcript = data?.transcript ?? { wordTranscript: [], speakerTranscript: [] };
    let text = '';
    if (transcript.speakerTranscript?.length > 0) {
      text = transcript.speakerTranscript.map((seg) => seg.text).join('\n\n');
    } else if (transcript.wordTranscript?.length > 0) {
      text = transcript.wordTranscript
        .map((para) => para.map((w) => w.word).join(' '))
        .join('\n\n');
    }
    if (!text) return;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'transcript.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleBackToVideos = () => {
    navigate(`/${state}/videos`);
  };

  const billActionGroupCount = data?.billActions?.length ?? 0;
  const billActionEventCount = (data?.billActions ?? []).reduce(
    (sum, bill) => sum + bill.actions.length,
    0
  );
  const witnessCount = data?.witnesses?.length ?? 0;
  const headingName = data?.videoDetails?.name || 'Committee Session';
  const subtitle = data?.videoDetails?.title || 'Recorded committee session';

  if (isLoadingDetails || !data) {
    return <MainLoader />;
  }

  const tabBaseClass =
    'rounded-full border border-transparent px-4 py-2 text-sm transition-colors data-[state=active]:border-slate-200 data-[state=active]:bg-white data-[state=active]:shadow-sm hover:bg-white/60';

  return (
    <div className="min-h-screen">
      <div className="w-full px-4 py-4 sm:px-6 sm:py-6 space-y-4">
        <div className="rounded-[28px] border border-slate-200 bg-white/95 shadow-[0_24px_60px_-52px_rgba(15,23,42,0.65)]">
          <div className="p-4 sm:p-5">
            <Button
              variant="ghost"
              onClick={handleBackToVideos}
              className="mb-3 h-auto px-0 text-slate-600 hover:bg-transparent hover:text-slate-900"
            >
              <IoArrowBack className="mr-2 h-4 w-4" />
              Back to Committee Videos
            </Button>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
                    {headingName}
                  </h1>
                  {data?.videoDetails?.time ? (
                    <Badge
                      variant="secondary"
                      className="border border-slate-200 bg-slate-50 text-slate-700"
                    >
                      <IoTimeOutline className="mr-1.5 h-3.5 w-3.5" />
                      {data.videoDetails.time}
                    </Badge>
                  ) : null}
                </div>
                <p className="max-w-3xl text-sm leading-6 text-slate-600">
                  {state ? state.toUpperCase() : ''}
                  {subtitle ? ` • ${subtitle}` : ''}
                </p>
              </div>

              <div className="flex flex-wrap gap-2 text-sm text-slate-600">
                {billActionGroupCount > 0 && (
                  <div className="rounded-full border border-slate-200 bg-slate-50 px-4 py-1.5">
                    <span className="font-semibold text-slate-900">
                      {billActionGroupCount}
                    </span>{' '}
                    bills
                  </div>
                )}
                {billActionEventCount > 0 && (
                  <div className="rounded-full border border-slate-200 bg-slate-50 px-4 py-1.5">
                    <span className="font-semibold text-slate-900">
                      {billActionEventCount}
                    </span>{' '}
                    actions
                  </div>
                )}
                {witnessCount > 0 && (
                  <div className="rounded-full border border-slate-200 bg-slate-50 px-4 py-1.5">
                    <span className="font-semibold text-slate-900">{witnessCount}</span>{' '}
                    witnesses
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          className={`${
            isVideoExpanded
              ? 'grid grid-cols-1'
              : 'grid grid-cols-1 xl:grid-cols-[minmax(0,52rem)_minmax(30rem,1fr)] 2xl:grid-cols-[minmax(0,48rem)_minmax(30rem,1fr)]'
          } gap-4 lg:gap-6`}
        >
          {/* Left column: video + below tabs */}
          <div
            className={`min-w-0 space-y-6 ${
              isVideoExpanded ? 'xl:col-span-12' : 'xl:max-w-[52rem] 2xl:max-w-[48rem]'
            } xl:sticky xl:top-6 self-start`}
          >
            <div className="bg-slate-50 rounded-xl shadow-lg shadow-slate-300/50 border border-slate-200/60 overflow-hidden relative">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="space-y-1">
                    <h2 className="text-lg font-semibold text-slate-800">Recorded Session</h2>
                    <p className="text-sm text-slate-500">
                      {data?.videoDetails?.title || 'Committee Recording'}
                    </p>
                  </div>
                </div>

                <div className="relative bg-black rounded-lg overflow-hidden aspect-video mb-4 group">
                  <video ref={videoRef} controls className="w-full h-full object-cover">
                    <track
                      kind="captions"
                      src="data:text/vtt,WEBVTT"
                      srcLang="en"
                      label="English captions unavailable"
                      default
                    />
                  </video>
                  <div className="absolute top-4 left-4 bg-black bg-opacity-70 text-white px-2 py-1 rounded-md text-sm">
                    {data?.videoDetails?.time}
                  </div>
                  <div className="absolute top-4 right-4">
                    <Badge variant="outline" className="bg-red-600 text-white border-none">
                      {data?.videoDetails?.status}
                    </Badge>
                  </div>
                  {!isSmallScreen && (
                    <button
                      type="button"
                      aria-label={
                        isVideoExpanded ? 'Restore split view' : 'Expand video to full width'
                      }
                      title={isVideoExpanded ? 'Restore split view' : 'Expand to full width'}
                      onClick={() => setUserExpandedVideo((prev) => !prev)}
                      className="absolute top-1/2 right-0 z-10 -translate-y-1/2 flex items-center justify-center h-16 w-7 rounded-l-md bg-slate-900/75 hover:bg-slate-900/95 text-white shadow-lg ring-1 ring-white/10 transition-colors cursor-pointer"
                    >
                      {isVideoExpanded ? (
                        <IoChevronBack className="h-5 w-5" />
                      ) : (
                        <IoChevronForward className="h-5 w-5" />
                      )}
                    </button>
                  )}
                </div>

                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={handleDownloadTranscript}>
                      <LuFileText className="h-4 w-4 mr-1" />
                      Download Transcript
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Below-video tabs */}
            <div className="bg-slate-50 rounded-xl shadow-lg shadow-slate-300/50 border border-slate-200/60 overflow-hidden">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="h-auto w-full flex flex-wrap items-center justify-start gap-2 border-b border-slate-200 bg-slate-50/90 p-3">
                  {isVideoExpanded && (
                    <TabsTrigger value="transcript" className={tabBaseClass}>
                      Transcript
                    </TabsTrigger>
                  )}
                  {isVideoExpanded && (
                    <TabsTrigger value="summary" className={tabBaseClass}>
                      Summary
                    </TabsTrigger>
                  )}
                  {isVideoExpanded && data?.summary?.length > 0 && (
                    <TabsTrigger value="keypoints" className={tabBaseClass}>
                      Key Points
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="assistant" className={tabBaseClass}>
                    AI Assistant
                  </TabsTrigger>
                  {data?.billActions?.length ? (
                    <TabsTrigger value="billActions" className={tabBaseClass}>
                      Bill Actions
                    </TabsTrigger>
                  ) : null}
                  {data?.witnesses?.length ? (
                    <TabsTrigger value="witnesses" className={tabBaseClass}>
                      Witnesses
                    </TabsTrigger>
                  ) : null}
                </TabsList>

                {isVideoExpanded && (
                  <TabsContent value="transcript" className="p-4">
                    <RichTranscript
                      searchText={queryText}
                      transcript={data?.transcript ?? null}
                      onGoToTimeStamp={onGoToTimeStamp}
                      initialSearchTerm={queryText || undefined}
                    />
                  </TabsContent>
                )}

                {isVideoExpanded && (
                  <TabsContent value="summary" className="p-4">
                    <h2 className="text-lg font-medium mb-4 text-slate-800">Summary</h2>
                    <div className="overflow-y-auto h-[calc(100vh-260px)]">
                      {data?.nonTimestampSummary ? (
                        <div className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
                          {data.nonTimestampSummary}
                        </div>
                      ) : (
                        <div className="rounded-lg border border-slate-200 bg-slate-100/40 p-4 text-sm text-slate-500">
                          No summary available for this hearing yet.
                        </div>
                      )}
                    </div>
                  </TabsContent>
                )}

                {isVideoExpanded && data?.summary?.length > 0 && (
                  <TabsContent value="keypoints" className="p-4">
                    <KeyPointsPanel items={data.summary} onGoToTimeStamp={onGoToTimeStamp} />
                  </TabsContent>
                )}

                <TabsContent value="assistant" className="p-4">
                  <div className="space-y-4">
                    <div className="bg-slate-100/60 rounded-lg p-4 h-[22rem] overflow-y-auto">
                      <div className="flex flex-col space-y-3">
                        {aiChat.map((chat, idx) => {
                          const isUser = chat.Id === 'user';
                          return (
                            <div key={idx} className={`flex ${isUser ? 'justify-end' : ''}`}>
                              <div
                                className={`p-3 rounded-lg max-w-xs break-words whitespace-pre-wrap ${
                                  isUser
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-white text-slate-800 shadow-sm'
                                }`}
                              >
                                {chat.message}
                              </div>
                            </div>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Input
                        placeholder="Ask a question about this video..."
                        value={userQuestion}
                        onChange={(e) => setUserQuestion(e.target.value)}
                        className="flex-1"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSubmitQuestion();
                        }}
                      />
                      <Button onClick={handleSubmitQuestion} disabled={aiLoading}>
                        {aiLoading ? (
                          <IoRefresh className="h-4 w-4 animate-spin" />
                        ) : (
                          <IoSend className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <div className="pt-2">
                      <p className="text-xs text-slate-500 mb-2">Suggested questions:</p>
                      <div className="flex flex-wrap gap-2">
                        {(data?.suggested_questions || []).map((item, index) => (
                          <Button
                            key={index}
                            variant="outline"
                            size="sm"
                            className="text-xs"
                            onClick={() => setUserQuestion(item.question)}
                          >
                            {item.question}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {data?.billActions?.length ? (
                  <TabsContent value="billActions" className="px-5 py-4 sm:px-6">
                    <BillActionsPanel
                      billActions={data.billActions}
                      eventCount={billActionEventCount}
                      onGoToTimeStamp={onGoToRecordedVideoTime}
                      stateCode={state}
                    />
                  </TabsContent>
                ) : null}

                {data?.witnesses?.length ? (
                  <TabsContent value="witnesses" className="px-5 py-4 sm:px-6">
                    <WitnessesPanel
                      witnesses={data.witnesses}
                      stateCode={state}
                      onWitnessClick={handleWitnessClick}
                      onAssigned={() => {
                        if (videoId && state) dispatch(getVideoDetails({ videoId, state }));
                      }}
                    />
                  </TabsContent>
                ) : null}
              </Tabs>
            </div>
          </div>

          {/* Right column: split-view tabs (when not expanded) */}
          {!isVideoExpanded && (
            <div className="min-w-0 space-y-6">
              <div className="bg-slate-50 rounded-xl shadow-lg shadow-slate-300/50 border border-slate-200/60 overflow-hidden">
                <Tabs
                  value={activeRightTab}
                  onValueChange={setActiveRightTab}
                  className="w-full"
                >
                  <TabsList className="w-full grid grid-cols-3 gap-1 h-auto p-1">
                    <TabsTrigger value="transcript" className={`${tabBaseClass} text-sm`}>
                      Transcript
                    </TabsTrigger>
                    <TabsTrigger value="summary" className={`${tabBaseClass} text-sm`}>
                      Summary
                    </TabsTrigger>
                    <TabsTrigger value="keypoints" className={`${tabBaseClass} text-sm`}>
                      Key Points
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="transcript" className="p-4">
                    <RichTranscript
                      searchText={queryText}
                      transcript={data?.transcript ?? null}
                      onGoToTimeStamp={onGoToTimeStamp}
                      initialSearchTerm={queryText || undefined}
                    />
                  </TabsContent>
                  <TabsContent value="summary" className="p-4">
                    <h2 className="text-lg font-medium mb-4 text-slate-800">Summary</h2>
                    <div className="overflow-y-auto h-[calc(100vh-260px)]">
                      {data?.nonTimestampSummary ? (
                        <div className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
                          {data.nonTimestampSummary}
                        </div>
                      ) : (
                        <div className="rounded-lg border border-slate-200 bg-slate-100/40 p-4 text-sm text-slate-500">
                          No summary available for this hearing yet.
                        </div>
                      )}
                    </div>
                  </TabsContent>
                  <TabsContent value="keypoints" className="p-4">
                    <KeyPointsPanel
                      items={data?.summary ?? []}
                      onGoToTimeStamp={onGoToTimeStamp}
                    />
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const KeyPointsPanel = ({ items, onGoToTimeStamp }) => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const all = (items || []).filter((i) => (i.summary || '').trim().length > 0);
    if (!search.trim()) return all;
    const q = search.trim().toLowerCase();
    return all.filter((i) => i.summary.toLowerCase().includes(q));
  }, [items, search]);

  if (!items || items.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-100/40 p-4 text-sm text-slate-500">
        No key points available.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Input
          placeholder="Filter key points by word or phrase..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <p className="text-xs text-slate-500">
          {search.trim()
            ? `Showing ${filtered.length} of ${items.length} key points for "${search.trim()}".`
            : `Showing ${items.length} key points.`}
        </p>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-100/40 px-4 py-6 text-sm text-slate-500">
          No key points match "{search.trim()}".
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((item, i) => {
            const hasStartTime = typeof item.startTime === 'number' && item.startTime > 0;
            return (
              <div
                key={i}
                className="rounded-2xl border border-slate-200 bg-slate-100/60 p-4 shadow-sm sm:p-5"
              >
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                      Key Point {String(i + 1).padStart(2, '0')}
                    </p>
                    {hasStartTime ? (
                      <Badge
                        variant="secondary"
                        className="border border-slate-200 bg-white/80 text-slate-700"
                      >
                        <IoTimeOutline className="mr-1.5 h-3.5 w-3.5" />
                        {formatRecordedTimestamp(item.startTime / 10)}
                      </Badge>
                    ) : null}
                  </div>
                  <Button
                    onClick={() => onGoToTimeStamp(item.startTime || 0)}
                    size="sm"
                    className="self-start"
                    disabled={!hasStartTime}
                  >
                    Go to this section
                  </Button>
                </div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{item.summary}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const BillActionsPanel = ({ billActions, eventCount, onGoToTimeStamp, stateCode }) => (
  <div>
    <div className="mb-5 flex items-baseline justify-between">
      <p className="text-xs tracking-wide text-slate-400">
        {eventCount} action{eventCount === 1 ? '' : 's'} · click timestamps to jump
      </p>
    </div>

    <div className="divide-y divide-slate-100">
      {billActions.map((bill, billIndex) => (
        <div
          key={bill.masterBillId ?? bill.bill ?? `bill-${billIndex}`}
          className="py-4 first:pt-0 last:pb-0"
        >
          <div className="flex items-baseline gap-3">
            {bill.masterBillId ? (
              <Link
                to={`/${stateCode}/bill/${bill.masterBillId}`}
                className="text-[15px] font-semibold text-slate-900 hover:text-blue-600"
              >
                {formatCommitteeBillLabel(bill.bill)}
              </Link>
            ) : (
              <span className="text-[15px] font-semibold text-slate-900">
                {formatCommitteeBillLabel(bill.bill)}
              </span>
            )}
            <span className="text-xs text-slate-400">
              {bill.actions.length} action{bill.actions.length === 1 ? '' : 's'}
            </span>
          </div>

          {bill.title ? (
            <p className="mt-0.5 text-[13px] leading-5 text-slate-500">{bill.title}</p>
          ) : null}

          <div className="mt-2.5 space-y-1.5">
            {bill.actions.map((action) => (
              <div key={action.Id} className="group flex items-start gap-3">
                <button
                  onClick={() => onGoToTimeStamp(action.StartTime)}
                  className="mt-px shrink-0 font-mono text-xs font-medium tabular-nums text-slate-400 transition-colors hover:text-blue-600"
                >
                  {formatRecordedTimestamp(action.StartTime)}
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] leading-5 text-slate-700">
                    {getCommitteeActionDescription(action)}
                  </p>
                  {action.ContextSentence ? (
                    <p className="mt-0.5 text-[13px] leading-5 text-slate-400">
                      {action.ContextSentence}
                    </p>
                  ) : null}
                  {action.Outcome ? (
                    <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wider text-slate-400">
                      {action.Outcome}
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const WitnessesPanel = ({ witnesses, stateCode, onWitnessClick, onAssigned }) => (
  <div>
    <div className="mb-5">
      <p className="text-xs tracking-wide text-slate-400">
        {witnesses.length} witness{witnesses.length === 1 ? '' : 'es'} · click timestamps to jump
      </p>
    </div>

    <div className="divide-y divide-slate-100">
      {witnesses.map((witness) => (
        <div
          key={witness.witnessId}
          className="group cursor-pointer py-4 first:pt-0 last:pb-0"
          role="button"
          tabIndex={0}
          onClick={() => {
            const first = witness.timestamps[0];
            if (first?.startTime) onWitnessClick(first.startTime);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              const first = witness.timestamps[0];
              if (first?.startTime) {
                event.preventDefault();
                onWitnessClick(first.startTime);
              }
            }
          }}
        >
          <div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {witness.lobbyistId ? (
                <Link
                  to={`/${stateCode}/lobbyist/${witness.lobbyistId}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-[15px] font-semibold text-blue-500 hover:text-blue-600"
                >
                  {witness.name}
                </Link>
              ) : (
                <span className="text-[15px] font-semibold text-slate-900">{witness.name}</span>
              )}
              <WitnessLobbyistAssignment
                witnessId={witness.witnessId}
                witnessName={witness.name}
                stateCode={stateCode}
                currentLobbyistId={witness.lobbyistId}
                onAssigned={onAssigned}
              />
              {witness.timestamps.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {witness.timestamps.map((ts, idx) =>
                    ts.startTime ? (
                      <button
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (ts.startTime) onWitnessClick(ts.startTime);
                        }}
                        className="font-mono text-xs font-medium tabular-nums text-slate-400 transition-colors hover:text-blue-600"
                      >
                        {ts.startTime}
                      </button>
                    ) : null
                  )}
                </div>
              )}
            </div>

            {witness.organization && (
              <p className="mt-0.5 text-[13px] text-slate-500">
                {witness.companyId ? (
                  <Link
                    to={`/${stateCode}/company/${witness.companyId}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-blue-500 hover:text-blue-600"
                  >
                    {witness.organization}
                  </Link>
                ) : (
                  witness.organization
                )}
              </p>
            )}

            {witness.bills && witness.bills.length > 0 && (
              <div className="mt-2 space-y-1">
                {witness.bills.map((bill) => (
                  <div key={bill.masterBillId} className="flex items-baseline gap-2 flex-wrap">
                    <Link
                      to={`/${stateCode}/bill/${bill.masterBillId}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-[13px] font-medium text-slate-600 hover:text-blue-600"
                    >
                      {bill.billNumber}
                    </Link>
                    {bill.position && (
                      <span
                        className={`text-[11px] font-medium uppercase tracking-wider ${
                          bill.position.toLowerCase() === 'support'
                            ? 'text-emerald-600'
                            : bill.position.toLowerCase() === 'oppose'
                              ? 'text-red-500'
                              : 'text-slate-400'
                        }`}
                      >
                        {bill.position}
                      </span>
                    )}
                    {bill.title && (
                      <span className="text-[12px] text-slate-400">{bill.title}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default Video;
